/**
 * Step 0 of the instant-quote funnel: lets the user pick which type of
 * service they need (windows, sliding doors, or both). Renders three
 * tactile SVG cards because real product photos don't scale well at this
 * size and lose impact on mobile.
 *
 * Pure presentational — owns no state. Parent holds `formData.serviceType`
 * and passes the setter.
 */
const OPTIONS = [
  {
    value: "windows",
    label: "Window Screens",
    icon: (
      <svg viewBox="0 0 48 48" className="w-9 h-9" aria-hidden="true">
        {/* outer window frame */}
        <rect x="6" y="4" width="36" height="40" rx="2" fill="none" stroke="#207024" strokeWidth="2.5" />
        {/* center mullion */}
        <line x1="24" y1="4" x2="24" y2="44" stroke="#207024" strokeWidth="2.5" />
        {/* mid rail */}
        <line x1="6" y1="24" x2="42" y2="24" stroke="#207024" strokeWidth="2.5" />
        {/* mesh — vertical */}
        {[10, 14, 18, 28, 32, 36].map((x) => (
          <line key={`v${x}`} x1={x} y1="6" x2={x} y2="42" stroke="#207024" strokeWidth="0.5" opacity="0.55" />
        ))}
        {/* mesh — horizontal */}
        {[8, 12, 16, 20, 28, 32, 36, 40].map((y) => (
          <line key={`h${y}`} x1="7" y1={y} x2="41" y2={y} stroke="#207024" strokeWidth="0.5" opacity="0.55" />
        ))}
      </svg>
    ),
  },
  {
    value: "doors",
    label: "Sliding Screen Doors",
    icon: (
      <svg viewBox="0 0 48 48" className="w-9 h-9" aria-hidden="true">
        {/* track top */}
        <line x1="3" y1="5" x2="45" y2="5" stroke="#207024" strokeWidth="1.5" />
        {/* track bottom */}
        <line x1="3" y1="43" x2="45" y2="43" stroke="#207024" strokeWidth="1.5" />
        {/* left (fixed) glass panel — light fill */}
        <rect x="6" y="6" width="18" height="36" rx="1" fill="#e5f0e6" stroke="#207024" strokeWidth="2" />
        {/* right (screen) panel — meshed */}
        <rect x="24" y="6" width="18" height="36" rx="1" fill="none" stroke="#207024" strokeWidth="2.5" />
        {/* screen mesh */}
        {[27, 30, 33, 36, 39].map((x) => (
          <line key={`dv${x}`} x1={x} y1="8" x2={x} y2="40" stroke="#207024" strokeWidth="0.5" opacity="0.6" />
        ))}
        {[10, 14, 18, 22, 26, 30, 34, 38].map((y) => (
          <line key={`dh${y}`} x1="25" y1={y} x2="41" y2={y} stroke="#207024" strokeWidth="0.5" opacity="0.6" />
        ))}
        {/* handle */}
        <rect x="36.5" y="22" width="1.5" height="6" rx="0.5" fill="#207024" />
        {/* slide-arrow hint */}
        <path d="M19 24 L15 24 M15 24 L17 22 M15 24 L17 26" stroke="#207024" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: "both",
    label: "Both",
    icon: (
      <svg viewBox="0 0 48 48" className="w-9 h-9" aria-hidden="true">
        {/* small window on the left */}
        <rect x="4" y="10" width="18" height="28" rx="1.5" fill="none" stroke="#207024" strokeWidth="2" />
        <line x1="13" y1="10" x2="13" y2="38" stroke="#207024" strokeWidth="2" />
        <line x1="4" y1="24" x2="22" y2="24" stroke="#207024" strokeWidth="2" />
        {/* small slider on the right */}
        <line x1="24" y1="7" x2="46" y2="7" stroke="#207024" strokeWidth="1.2" />
        <line x1="24" y1="41" x2="46" y2="41" stroke="#207024" strokeWidth="1.2" />
        <rect x="26" y="8" width="9" height="32" rx="0.8" fill="#e5f0e6" stroke="#207024" strokeWidth="1.6" />
        <rect x="35" y="8" width="9" height="32" rx="0.8" fill="none" stroke="#207024" strokeWidth="2" />
        {[37, 39, 41, 43].map((x) => (
          <line key={`bv${x}`} x1={x} y1="10" x2={x} y2="38" stroke="#207024" strokeWidth="0.4" opacity="0.6" />
        ))}
        {[12, 16, 20, 24, 28, 32, 36].map((y) => (
          <line key={`bh${y}`} x1="36" y1={y} x2="43" y2={y} stroke="#207024" strokeWidth="0.4" opacity="0.6" />
        ))}
      </svg>
    ),
  },
];

const QUESTION = "What do you need help with today?";

export default function ServiceTypeStep({ value, onSelect, variant }) {
  const custom = variant && variant.id !== "A";
  return (
    <div className="space-y-6" data-testid="step-service-type" data-variant={variant?.id || "A"}>
      {custom ? (
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 leading-tight" data-testid="variant-headline">{variant.headline}</h1>
            {variant.subhead && <p className="text-zinc-600 mt-2" data-testid="variant-subhead">{variant.subhead}</p>}
          </div>
          <p className="text-base font-semibold text-zinc-800 pt-1" data-testid="service-type-question">{QUESTION}</p>
        </div>
      ) : (
        <h1 className="text-2xl font-bold text-zinc-900">{QUESTION}</h1>
      )}
      <div className="space-y-3">
        {OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => onSelect(option.value)}
            data-testid={`service-type-${option.value}-btn`}
            className={`w-full p-5 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
              value === option.value
                ? "border-[#207024] bg-green-50"
                : "border-zinc-200 bg-white hover:border-zinc-300"
            }`}
          >
            <span className="shrink-0">{option.icon}</span>
            <span className="text-lg font-semibold">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
